/* ============================================================
   AK Letters — collection grid cell loader

   Renders one customer letter into each [data-letters-cell] in
   the editorial collection grid. Unlike the PDP pull-quote
   (which is keyed to a single product), a collection cell is
   handed a POOL of the collection's product IDs. It shuffles
   them and queries Stamped product-by-product, stopping at the
   first product that yields a review meeting the rating +
   word-count bar — so it surfaces a strong in-collection voice
   without needing sales data, and naturally favours products
   with more reviews.

   Mirrors the PDP loader's fetch + filter + name/city handling
   (assets/ak-letters-quote.js); the quote is truncated tighter
   to fit a grid cell, and there is no inline expander or stars.

   Graceful failure: a cell that finds nothing eligible (or when
   Stamped is unavailable) gets .is-empty and collapses, so the
   grid never shows an empty frame.
   ============================================================ */
(function () {
  if (window.__akLettersCellInit) return;
  window.__akLettersCellInit = true;

  /* Per-product fetch cache so multiple cells (or retries) don't
     refetch the same product widget. */
  var widgetCache = {};

  function titleCaseToken(token) {
    if (!token) return token;
    return token.split(/([-'])/).map(function (piece) {
      if (piece === '-' || piece === "'") return piece;
      if (!piece) return piece;
      return piece.charAt(0).toUpperCase() + piece.slice(1).toLowerCase();
    }).join('');
  }

  function abbreviateName(raw) {
    if (!raw) return '';
    var parts = raw.trim().split(/\s+/);
    if (parts.length === 1) return titleCaseToken(parts[0]);
    return titleCaseToken(parts[0]) + ' '
      + titleCaseToken(parts[parts.length - 1]).charAt(0) + '.';
  }

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  /* Tighter than the PDP truncation so the quote fits a grid cell.
     Walk whole sentences; never chop mid-sentence (looks careless
     on a luxury page). If a single sentence already exceeds the
     cap, show it whole rather than cutting it. */
  function truncate(body) {
    var TARGET = 28, CAP = 38, SHORT = 34;
    var wc = body.split(/\s+/).filter(Boolean).length;
    if (wc <= SHORT) return body;
    var sentences = body.match(/[^.!?]+[.!?]+(\s+|$)/g);
    if (!sentences || sentences.length <= 1) return body;
    var visible = '', vw = 0;
    for (var i = 0; i < sentences.length; i++) {
      var sw = sentences[i].trim().split(/\s+/).filter(Boolean).length;
      if (visible && vw + sw > CAP) break;
      visible += sentences[i];
      vw += sw;
      if (vw >= TARGET) break;
    }
    return visible.trim() || body;
  }

  function getStampedOpts() {
    var opts = window.StampedGlobalOptions || {};
    if (!opts.apiKey || (!opts.storeUrl && !opts.sId)) return null;
    return opts;
  }

  function buildUrl(opts, productId) {
    var storeName = (window.Shopify && window.Shopify.shop) || window.location.host;
    var url = 'https://stamped.io/api/widget'
      + '?productId=' + encodeURIComponent(productId)
      + '&apiKey=' + encodeURIComponent(opts.apiKey)
      + '&take=50';
    if (opts.storeUrl) {
      url += '&storeUrl=' + encodeURIComponent(opts.storeUrl);
    } else {
      url += '&sId=' + encodeURIComponent(opts.sId)
        + '&storeName=' + encodeURIComponent(storeName);
    }
    return url;
  }

  /* Fetch + filter one product's reviews into named/anon pools.
     Returns a cached promise of { named: [], anon: [] }. */
  function fetchEligible(opts, productId, minRating, minWords) {
    if (widgetCache[productId]) return widgetCache[productId];
    var p = fetch(buildUrl(opts, productId), { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        var html = json && json.widget;
        if (!html) return { named: [], anon: [] };
        var doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
        var cards = doc.querySelectorAll('.stamped-review');
        var named = [], anon = [];
        Array.prototype.forEach.call(cards, function (card) {
          var rating = parseFloat(card.getAttribute('data-rating') || '0');
          if (rating < minRating) return;
          var bodyEl = card.querySelector('.stamped-review-content-body')
            || card.querySelector('.stamped-review-body p')
            || card.querySelector('.stamped-review-body');
          var body = bodyEl ? bodyEl.textContent.trim() : '';
          if (!body) return;
          if (body.split(/\s+/).length < minWords) return;
          var authorEl = card.querySelector('.stamped-review-header .author, .author');
          var locEl = card.querySelector('.stamped-review-header .review-location, .review-location');
          var author = authorEl ? authorEl.textContent.trim() : '';
          var location = locEl ? locEl.textContent.trim() : '';
          var lc = author.toLowerCase();
          if (!author || lc === 'anonymous' || lc === 'anon') {
            anon.push({ body: body, author: '', location: location });
          } else {
            named.push({ body: body, author: author, location: location });
          }
        });
        return { named: named, anon: anon };
      })
      .catch(function () { return { named: [], anon: [] }; });
    widgetCache[productId] = p;
    return p;
  }

  function empty(cell) {
    cell.classList.remove('is-loading');
    cell.classList.add('is-empty');
  }

  function render(cell, review) {
    var body = (review.body || '').trim();
    var name = (review.author || '').trim();
    var city = (review.location || '').trim();
    if (city.indexOf(',') > -1) city = city.split(',')[0].trim();
    if (!body) { empty(cell); return; }

    var quoteEl = cell.querySelector('[data-quote-text]');
    var nameEl = cell.querySelector('[data-quote-name]');
    var cityEl = cell.querySelector('[data-quote-city]');
    var sepEl = cell.querySelector('[data-quote-sep]');

    quoteEl.textContent = truncate(body);

    var displayName = abbreviateName(name);
    if (displayName) nameEl.textContent = displayName;
    else nameEl.style.display = 'none';

    if (city) cityEl.textContent = city;
    else cityEl.style.display = 'none';

    if (!displayName || !city) sepEl.style.display = 'none';

    cell.classList.remove('is-loading');
    cell.classList.add('is-ready');
  }

  function loadCell(cell, opts) {
    var ids = (cell.dataset.productIds || '').split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
    var minRating = parseFloat(cell.dataset.minRating) || 5;
    var minWords = parseInt(cell.dataset.minWords, 10) || 8;
    if (!ids.length) { empty(cell); return; }

    shuffle(ids);
    var maxTries = Math.min(ids.length, 8);
    var i = 0;

    function next() {
      if (i >= maxTries) { empty(cell); return; }
      var id = ids[i++];
      fetchEligible(opts, id, minRating, minWords).then(function (pools) {
        var pool = pools.named.length ? pools.named : pools.anon;
        if (pool.length) {
          render(cell, pool[Math.floor(Math.random() * pool.length)]);
        } else {
          next();
        }
      });
    }
    next();
  }

  function init() {
    var cells = document.querySelectorAll('[data-letters-cell]');
    if (!cells.length) return;

    var attempts = 0, maxAttempts = 20;
    function poll() {
      var opts = getStampedOpts();
      if (opts) {
        Array.prototype.forEach.call(cells, function (cell) { loadCell(cell, opts); });
        return;
      }
      if (++attempts < maxAttempts) {
        setTimeout(poll, 500);
      } else {
        Array.prototype.forEach.call(cells, empty);
      }
    }
    poll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
